import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { Flip } from 'gsap/Flip';
import { useCallback, useRef } from 'react';
import { BlogPostDto } from '@/components/BlogPage/types';
import { gsap } from '@/lib/gsap';
gsap.registerPlugin(Flip);
interface UsePostsAnimationParams {
  containerRef: React.RefObject<HTMLDivElement | null>;
  displayedPosts: BlogPostDto[];
  viewMode: string;
}
export const usePostsAnimation = t0 => {
  const $ = _c(13);
  const {
    containerRef,
    displayedPosts,
    viewMode
  } = t0;
  const flipState = useRef(null);
  const containerHeightRef = useRef(0);
  const isTransitioningFromEmpty = useRef(false);
  let t1;
  if ($[0] !== containerRef) {
    t1 = async (currentPosts, nextPosts) => {
      if (!containerRef.current) {
        return;
      }
      const nextIds = new Set(nextPosts.map(_temp));
      const leavingPosts = currentPosts.filter(p_0 => !nextIds.has(p_0.id));
      if (currentPosts.length === 0 && nextPosts.length > 0) {
        const emptyResult = containerRef.current.querySelector(".empty-result");
        if (emptyResult) {
          await gsap.to(emptyResult, {
            opacity: 0,
            scale: 0.9,
            duration: 0.3,
            ease: "power1.in"
          });
        }
      }
      if (leavingPosts.length > 0) {
        const leavingSelectors = leavingPosts.map(_temp2).join(",");
        const leavingElements = containerRef.current.querySelectorAll(leavingSelectors);
        if (leavingElements.length) {
          await gsap.to(leavingElements, {
            opacity: 0,
            scale: 0.9,
            duration: 0.3,
            ease: "power1.in"
          });
        }
      }
      containerHeightRef.current = containerRef.current.offsetHeight;
      if (currentPosts.length === 0 && nextPosts.length === 0) {
        isTransitioningFromEmpty.current = false;
        flipState.current = null;
      } else {
        if (currentPosts.length === 0) {
          isTransitioningFromEmpty.current = true;
          flipState.current = null;
        } else {
          isTransitioningFromEmpty.current = false;
          flipState.current = Flip.getState(".post-item");
        }
      }
    };
    $[0] = containerRef;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const prepareTagTransition = t1;
  let t2;
  if ($[2] !== containerRef || $[3] !== displayedPosts) {
    t2 = () => {
      if (!containerRef.current) {
        return;
      }
      if (containerRef.current.style.opacity === "0") {
        gsap.to(containerRef.current, {
          opacity: 1,
          duration: 0.2
        });
      }
      if (!displayedPosts.length) {
        gsap.fromTo(".empty-result", {
          opacity: 0,
          scale: 0.9
        }, {
          opacity: 1,
          scale: 1,
          duration: 0.3,
          ease: "power1.out",
          delay: 0.1
        });
      }
      if (isTransitioningFromEmpty.current) {
        const oldHeight = containerHeightRef.current;
        const newHeight = containerRef.current.offsetHeight;
        if (oldHeight) {
          gsap.fromTo(containerRef.current, {
            height: oldHeight
          }, {
            height: newHeight,
            duration: 0.4,
            ease: "power1.inOut",
            clearProps: "height"
          });
        }
        gsap.fromTo(".post-item", {
          opacity: 0,
          scale: 0.9
        }, {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          stagger: 0.05,
          ease: "power2.out",
          delay: 0.1
        });
        isTransitioningFromEmpty.current = false;
        containerHeightRef.current = 0;
        return;
      }
      if (flipState.current) {
        const oldHeight_0 = containerHeightRef.current;
        const newHeight_0 = containerRef.current.offsetHeight;
        if (oldHeight_0) {
          gsap.fromTo(containerRef.current, {
            height: oldHeight_0
          }, {
            height: newHeight_0,
            duration: 0.4,
            ease: "power1.inOut",
            clearProps: "height"
          });
        }
        Flip.from(flipState.current, {
          targets: ".post-item",
          duration: 0.4,
          ease: "power1.inOut",
          onEnter: _temp3
        });
        flipState.current = null;
        containerHeightRef.current = 0;
      }
    };
    $[2] = containerRef;
    $[3] = displayedPosts;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== displayedPosts || $[6] !== viewMode) {
    t3 = [displayedPosts, viewMode];
    $[5] = displayedPosts;
    $[6] = viewMode;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== containerRef || $[9] !== t3) {
    t4 = {
      dependencies: t3,
      scope: containerRef
    };
    $[8] = containerRef;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useGSAP(t2, t4);
  let t5;
  if ($[11] !== prepareTagTransition) {
    t5 = {
      prepareTagTransition
    };
    $[11] = prepareTagTransition;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
function _temp(p) {
  return p.id;
}
function _temp2(p_1) {
  return `[data-flip-id="${p_1.id}"]`;
}
function _temp3(elements) {
  gsap.fromTo(elements, {
    opacity: 0,
    scale: 0.9
  }, {
    opacity: 1,
    scale: 1,
    duration: 0.3,
    delay: 0.1
  });
}